// Live SVG jersey preview (§13.3 / D26 provisional art). Renders a shirt silhouette filled with the chosen
// pattern + colours and a crest badge, given the validated jersey spec (pattern ∈ 6, #RRGGBB colours, crest ∈ 24).
// SVG DOM only (no innerHTML). The crest art is provisional (D26) — shown as a numbered badge until M8's assets.

const NS = 'http://www.w3.org/2000/svg';
let clipSeq = 0;

export interface JerseySpec {
  pattern: string;
  primary: string;
  secondary: string;
  crest: string;
}

function svg<T extends SVGElement>(tag: string, attrs: Record<string, string | number>): T {
  const e = document.createElementNS(NS, tag) as T;
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

const SHIRT = 'M35 12 L20 20 L12 40 L22 46 L28 40 L28 98 L72 98 L72 40 L78 46 L88 40 L80 20 L65 12 C60 20 40 20 35 12 Z';

export function createJerseyPreview(j: JerseySpec, size = 120): SVGSVGElement {
  const root = svg<SVGSVGElement>('svg', { viewBox: '0 0 100 110', width: size, height: Math.round(size * 1.1) });
  const clipId = `jclip-${clipSeq++}`;
  const defs = svg('defs', {});
  const clip = svg('clipPath', { id: clipId });
  clip.append(svg('path', { d: SHIRT }));
  defs.append(clip);
  root.append(defs);

  root.append(svg('path', { d: SHIRT, fill: j.primary, stroke: '#00000055', 'stroke-width': 1 }));

  const g = svg('g', { 'clip-path': `url(#${clipId})` });
  const rect = (x: number, y: number, w: number, h: number): void => g.append(svg('rect', { x, y, width: w, height: h, fill: j.secondary }));
  switch (j.pattern) {
    case 'stripes':
      for (let x = 12; x < 88; x += 16) rect(x, 0, 8, 110);
      break;
    case 'hoops':
      for (let y = 8; y < 110; y += 16) rect(0, y, 100, 8);
      break;
    case 'halves':
      rect(0, 0, 50, 110);
      break;
    case 'quarters':
      rect(0, 0, 50, 55);
      rect(50, 55, 50, 55);
      break;
    case 'sash':
      g.append(svg('polygon', { points: '0,82 22,110 66,0 42,0', fill: j.secondary }));
      break;
    default:
      break; // solid — no overlay
  }
  root.append(g);

  const crestNum = j.crest.match(/\d+/)?.[0] ?? '?';
  root.append(svg('circle', { cx: 50, cy: 44, r: 10, fill: '#ffffffcc', stroke: j.secondary, 'stroke-width': 2 }));
  const label = svg('text', { x: 50, y: 48, 'text-anchor': 'middle', 'font-size': 10, fill: '#0a1f14', 'font-family': 'monospace' });
  label.textContent = crestNum;
  root.append(label);
  return root;
}
